import { useDeferredValue, useEffect, useState } from 'react';
import LoadingState from '../../components/common/LoadingState';
import { useAuth } from '../../hooks/useAuth';
import {
  createStaffUserDocument,
  listUserDocuments,
  USER_ROLES,
  updateUserRole,
} from '../../services/userService';
import { formatDate } from '../../utils/time';

const STAFF_ROLE_OPTIONS = [USER_ROLES.ADMIN, USER_ROLES.EDITOR, USER_ROLES.VIEWER];

export default function DashboardUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [savingRoleByUid, setSavingRoleByUid] = useState({});
  const [createPending, setCreatePending] = useState(false);
  const [newUser, setNewUser] = useState({
    uid: '',
    email: '',
    displayName: '',
    role: USER_ROLES.EDITOR,
  });

  const deferredSearchTerm = useDeferredValue(searchTerm);

  useEffect(() => {
    let isSubscribed = true;

    async function loadUsers() {
      setLoading(true);
      setErrorMessage('');

      try {
        const result = await listUserDocuments();
        if (!isSubscribed) {
          return;
        }
        setUsers(result);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }
        setErrorMessage(error.message ?? 'Failed to load admin users.');
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }

    loadUsers();

    return () => {
      isSubscribed = false;
    };
  }, []);

  const filteredUsers = users.filter((item) => {
    const token = deferredSearchTerm.trim().toLowerCase();
    if (!token) {
      return true;
    }

    return (
      item.uid?.toLowerCase().includes(token) ||
      item.email?.toLowerCase().includes(token) ||
      item.displayName?.toLowerCase().includes(token) ||
      item.role?.toLowerCase().includes(token)
    );
  });

  async function reloadUsers() {
    const result = await listUserDocuments();
    setUsers(result);
  }

  async function handleRoleSave(uid, role) {
    if (!uid) {
      return;
    }

    if (uid === user?.uid && role !== USER_ROLES.ADMIN) {
      setErrorMessage('You cannot remove your own admin role.');
      return;
    }

    setSavingRoleByUid((current) => ({ ...current, [uid]: true }));
    setErrorMessage('');

    try {
      await updateUserRole(uid, role);
      await reloadUsers();
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to update user role.');
    } finally {
      setSavingRoleByUid((current) => {
        const next = { ...current };
        delete next[uid];
        return next;
      });
    }
  }

  async function handleCreateUser(event) {
    event.preventDefault();
    setCreatePending(true);
    setErrorMessage('');

    try {
      await createStaffUserDocument(newUser);
      setNewUser({
        uid: '',
        email: '',
        displayName: '',
        role: USER_ROLES.EDITOR,
      });
      await reloadUsers();
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to create admin user document.');
    } finally {
      setCreatePending(false);
    }
  }

  if (loading) {
    return <LoadingState label="Loading admin users..." />;
  }

  return (
    <div className="page-stack">
      <section className="section-heading">
        <div>
          <p className="eyebrow">Admin Tools</p>
          <h1>Admin Users</h1>
          <p>
            Manage website login roles in Firestore (`users` collection). Public
            staff/team profiles are managed separately as data objects.
          </p>
        </div>
      </section>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      <form className="editor-form" onSubmit={handleCreateUser}>
        <h3>Add admin user document</h3>
        <label>
          UID
          <input
            value={newUser.uid}
            onChange={(event) =>
              setNewUser((current) => ({ ...current, uid: event.target.value }))
            }
            required
          />
        </label>

        <label>
          Email
          <input
            type="email"
            value={newUser.email}
            onChange={(event) =>
              setNewUser((current) => ({ ...current, email: event.target.value }))
            }
          />
        </label>

        <label>
          Display name
          <input
            value={newUser.displayName}
            onChange={(event) =>
              setNewUser((current) => ({
                ...current,
                displayName: event.target.value,
              }))
            }
          />
        </label>

        <label>
          Role
          <select
            value={newUser.role}
            onChange={(event) =>
              setNewUser((current) => ({ ...current, role: event.target.value }))
            }
          >
            {STAFF_ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </label>

        <button type="submit" disabled={createPending}>
          {createPending ? 'Adding admin user...' : 'Add admin user'}
        </button>
      </form>

      <div className="dashboard-toolbar">
        <input
          placeholder="Search users by uid, email, display name, or role..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="state-card">
          <p>No user documents found.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="content-table">
            <thead>
              <tr>
                <th>User</th>
                <th>UID</th>
                <th>Role</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((item) => (
                <tr key={item.uid || item.id}>
                  <td>
                    <p>{item.displayName || 'No name'}</p>
                    <p>{item.email || 'No email'}</p>
                  </td>
                  <td>{item.uid || item.id}</td>
                  <td>
                    <select
                      value={item.role || USER_ROLES.VIEWER}
                      onChange={(event) =>
                        setUsers((current) =>
                          current.map((record) =>
                            record.uid === item.uid
                              ? { ...record, role: event.target.value }
                              : record
                          )
                        )
                      }
                    >
                      {STAFF_ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      onClick={() =>
                        handleRoleSave(item.uid, item.role || USER_ROLES.VIEWER)
                      }
                      disabled={Boolean(savingRoleByUid[item.uid])}
                    >
                      {savingRoleByUid[item.uid] ? 'Saving...' : 'Save role'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
